import { REFRESH_TOKEN_EXPIRE_AT_COOKIE_KEY } from '@packages/shared/core/auth/constants';
import { HOUR_IN_MS } from '@packages/shared/core/common/constants';
import { ApiPathAskGateway } from '@packages/shared/core/common/constants/apiPaths';
import { Logger } from '@packages/shared/core/common/logger/interfaces';
import {
  AppEnvironmentService,
  CookiesService,
  CrossDomainStorage,
  HttpClient,
  PersistentStorageService,
} from '@packages/shared/core/common/services';
import { normalizeError } from '@packages/shared/core/common/utils/errors';
import { isEqual } from '@packages/shared/core/common/utils/isEqual';
import { FeatureFlags } from '@packages/shared/core/feature-flags/services';
import { UtmTags } from '@packages/shared/core/user/entities';
import { UtmTagsService } from '@packages/shared/core/user/services';
import { Config } from '@packages/shared/interfaces';
import { ArrivalService } from './ArrivalService';

type ArrivalTouch = {
  utmTags: UtmTags;
  referrerOrigin: string;
};

type LastArrival = ArrivalTouch & {
  sentAt: number;
};

type ArrivalEventDto = {
  event_name: string;
  event_time: number;
  split_id: string;
  utm_params: UtmTags;
  properties: {
    host: string;
    path: string;
    referrer: string;
    user_id: string | null;
    is_tester: boolean;
  };
};

export class ArrivalClientService implements ArrivalService {
  private readonly eventName = 'arrival';

  private readonly storageKey = 'nebula_last_arrival';

  private readonly lastArrivalLifetimeMs = 5 * HOUR_IN_MS;

  private readonly url = new URL(window.location.href);

  private isArrivalTracked = false;

  constructor(
    private readonly config: Config,
    private readonly featureFlags: FeatureFlags,
    private readonly appEnvironmentService: AppEnvironmentService,
    private readonly utmTagsService: UtmTagsService,
    private readonly askGatewayHttpClient: HttpClient,
    private readonly cookiesService: CookiesService,
    private readonly crossDomainStorage: CrossDomainStorage,
    private readonly persistentStorageService: PersistentStorageService,
    private readonly logger: Logger,
  ) {}

  trackArrival() {
    if (this.isArrivalTracked) return;

    this.isArrivalTracked = true;

    const touch: ArrivalTouch = {
      utmTags: this.utmTagsService.getQueryUtmTags(),
      referrerOrigin: this.getReferrerOrigin(),
    };

    if (!this.shouldSendArrival(touch)) return;

    void this.saveLastArrival(touch);
    void this.sendArrival(touch.utmTags);
  }

  private getReferrerOrigin(): string {
    try {
      return new URL(document.referrer).origin;
    } catch {
      return '';
    }
  }

  private shouldSendArrival({ utmTags, referrerOrigin }: ArrivalTouch): boolean {
    if (referrerOrigin === this.url.origin) return false;

    const lastArrival = this.getLastArrival();

    if (!lastArrival) return true;

    if (Object.keys(utmTags).length) {
      return !isEqual(utmTags, lastArrival.utmTags);
    }

    return referrerOrigin !== lastArrival.referrerOrigin;
  }

  private getLastArrival(): LastArrival | null {
    try {
      const lastArrival = this.persistentStorageService.get<LastArrival>(this.storageKey);

      if (!lastArrival || typeof lastArrival === 'string') return null;

      return Date.now() - lastArrival.sentAt < this.lastArrivalLifetimeMs ? lastArrival : null;
    } catch {
      return null;
    }
  }

  private async saveLastArrival(touch: ArrivalTouch) {
    try {
      await this.persistentStorageService.set(this.storageKey, { ...touch, sentAt: Date.now() });
    } catch (err) {
      this.logger.error(normalizeError(err));
    }
  }

  private async sendArrival(utmTags: UtmTags) {
    try {
      await this.askGatewayHttpClient.post(ApiPathAskGateway.SNITCHER_EVENT, {
        adapter: 'fetch',
        fetchOptions: { keepalive: true },
        headers: { locale: this.config.domainLocale },
        data: this.createArrivalEvent(utmTags),
      });
    } catch (err) {
      this.logger.error(normalizeError(err));
    }
  }

  private createArrivalEvent(utmTags: UtmTags): ArrivalEventDto {
    return {
      event_name: this.eventName,
      event_time: Date.now(),
      split_id: this.featureFlags.getSplitId(),
      utm_params: utmTags,
      properties: {
        host: this.url.host,
        path: this.url.pathname,
        referrer: document.referrer,
        user_id: this.getUserId(),
        is_tester: this.appEnvironmentService.isTestingEnv(),
      },
    };
  }

  private getUserId(): string | null {
    const refreshTokenExpiresAt = Number(
      this.cookiesService.get(REFRESH_TOKEN_EXPIRE_AT_COOKIE_KEY),
    );
    const isAuthorized = refreshTokenExpiresAt > Date.now();

    if (!isAuthorized) return null;

    return this.crossDomainStorage.getNebulaId() ?? null;
  }
}
